import {Component, Injector, Input, OnDestroy, ViewChild} from '@angular/core';
import {
    AbstractBackOfficeComponent,
    Actor,
    Circuit,
    CircuitStatus,
    CircuitUtils,
    CircuitViewUtils,
    DefaultState,
    FlagsService,
    LocaleEnum,
    PesDossier,
    PesDossierService,
    PesTask,
    Signatory,
    SubscriptionManager
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';
import {finalize} from 'rxjs/operators/finalize';
import {TranslateService} from '@ngx-translate/core';
import {StateDeclaration} from '@uirouter/angular';
import {NgbTabset} from '@ng-bootstrap/ng-bootstrap';
import {SafeHtml} from '@angular/platform-browser';

@Component({
    selector: 'app-lbpa-circuit-details',
    templateUrl: './lbpa-circuit-details.component.html'
})
export class LbpaCircuitDetailsComponent extends AbstractBackOfficeComponent implements OnDestroy {

    /**
     * @inheritDoc.
     */
    @Input() public backButtonTooltip: string = 'bo_circuits.details.back_button_label';

    /**
     * Hosting component for the tab set.
     */
    @ViewChild(NgbTabset) protected tabSet: NgbTabset;

    /**
     * The reference to the circuit managed in this view.
     */
    protected circuit: Circuit = null;

    /**
     * The subscriber
     * @protected
     */
    protected subscriber: Actor = null;

    /**
     * The page title.
     */
    protected pageTitle: string = null;
    /**
     * Show current task in the information tab
     */
    protected showCurrentTask: boolean = true;
    /**
     * Boolean for the button change token if the circuit is in advanced signature.
     */
    protected isPresential: boolean = false;
    /**
     * Boolean to show the number of signatories.
     */
    protected showNbSignatorie: boolean = false;
    /**
     * Boolean to show the expiration status.
     */
    protected showExpirationStatus: boolean = false;
    /**
     * The reference to the current state injected by angular.
     */
    protected currentState: StateDeclaration = null;
    /**
     * The reference to the circuit passed as route param.
     */
    private readonly _circuitRef: string = null;
    /**
     * The reference to the PesModalService singleton.
     */
    private readonly _translate: TranslateService = null;
    /**
     * The reference to the PesDossierService singleton.
     */
    private readonly _dossierService: PesDossierService = null;
    /**
     * The reference to the SVG flags resolver.
     */
    private readonly _flags: FlagsService = null;

    /**
     * Creates a new PesCircuitDetailsComponent instance.
     *
     * @param {Injector} injector the reference to the angular injector.
     */
    constructor(protected injector: Injector) {
        super(injector, DefaultState.BO_CIRCUITS);
        this.currentState = injector.get('$state$');
        this._circuitRef = decodeURI(this.state.params.circuitRef);
        this.showExpirationStatus = this.currentState.data['showExpirationStatus'];
        this._translate = injector.get(TranslateService);
        this._dossierService = injector.get(PesDossierService);
        this._flags = injector.get(FlagsService);
        this.backButtonParams = {restoreFilter: true};
        this.breadcrumbItems = [{label: 'bo_circuits.list.title', state: {url: DefaultState.BO_CIRCUITS}}];
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    /**
     * @inheritDoc
     */
    public refresh(): void {
        super.refresh();
        this.loading = true;
        const sub: ISubscription = this._dossierService.get(this._circuitRef)
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            ).subscribe((dossier: PesDossier) => {
                if (!dossier) {
                    this.backButtonClickHandler(null);
                } else {
                    this.circuit = CircuitUtils.fromDossier(dossier);
                    this.subscriber = this.circuit.actors.find(actor => actor.id === 'subscriber');
                    this.pageTitle = this.circuit.title;
                    this.breadcrumbItems.push({label: this.pageTitle});
                    this.isPresential = this.isPresentialInit();
                    this.showNbSignatorie = this.showNbSignatoriesInit();
                    this.showCurrentTask = this.showCurrentTaskStatus();
                    this.loading = false;
                }
            });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    /**
     * Display the number of signatories
     */
    public nbSignatories(): string {
        if (this.circuit.dossier.meta.nbSignatories) {
            return this._translate.instant('signatories.done', {
                'nbSigned': this.circuit.dossier.meta.nbSigned,
                'nbSignatories': this.circuit.dossier.meta.nbSignatories
            });
        }
        return null;
    }

    /**
     * Fixes reject reason for invalid phone num case.
     *
     * @param reason the reference to the original reason.
     * @returns the fixed reason.
     */
    public getRejectReasonKey(reason: string): string {
        return this._dossierService.getRejectReasonKey(reason);
    }

    /**
     * Returns the status icon resolved from the circuit status.
     *
     * @param {string} status the circuit status.
     * @return {string} the status icon class resolved from the circuit status.
     */
    protected getStatusIcon(circuit: Circuit): string {
        return CircuitViewUtils.getStatusIcon(circuit, this._translate);
    }

    /**
     * Returns a string that represents a SVG flag, depending on the specified locale.
     *
     * @param {LocaleEnum} locale the locale for which to return the SVG flag.
     * @returns {SafeHtml} the safe HTML representation of a SVG flag.
     */
    protected resolveLocaleFlag(locale: LocaleEnum): SafeHtml {
        return this._flags.getFlag(locale);
    }

    /**
     * Check if the currentTask is a sign type
     */
    private showNbSignatoriesInit(): boolean {
        return this.circuit.workflow.currentTask.type.startsWith('sign') && this.currentState.data['showNumberOfSignatories'];
    }

    /**
     * Returns a boolean that indicates whether to display the current task status (true), or not (false).
     *
     * @return {boolean} true whether to display the current task status; false otherwise.
     */
    private showCurrentTaskStatus(): boolean {
        const status: string = this.circuit.dossier.meta.status;
        return this.circuit.workflow && (status !== CircuitStatus.CANCELLED &&
            status !== CircuitStatus.FINISHED &&
            status !== CircuitStatus.EXPIRED);
    }

    /**
     * Return if the current task use the presential option.
     * @returns {boolean}
     */
    private isPresentialInit(): boolean {
        const actorId: string = this.circuit.workflow.currentTask.actor;
        const currentTaskId: string = this.circuit.workflow.currentTask.id;
        // Search current task
        const currentTask: PesTask = this.circuit.dossier.taskList.find(task => task.data.id === currentTaskId);
        if (!currentTask || !currentTask.data.taskData || !currentTask.data.taskData.signatories) {
            return false;
        }
        // Search current signatory
        const signatory: Signatory =
            currentTask.data.taskData.signatories.find((item: Signatory) => item.actorId === actorId);
        return signatory && signatory.user.presential !== null;
    }
}



// WEBPACK FOOTER //
// ./src/app/component/lbpa-circuit-details/lbpa-circuit-details.component.ts